import { sectionRenderer } from '@theme/section-renderer';
import { Component } from '@theme/component';
import { FilterUpdateEvent, ThemeEvents } from '@theme/events';
import { debounce, formatMoney, startViewTransition } from '@theme/utilities';

/**
 * Search query parameter.
 * @type {string}
 */
const SEARCH_QUERY = 'q';

/**
 * Handles the main facets form functionality
 *
 * @typedef {Object} FacetsFormRefs
 * @property {HTMLFormElement} facetsForm - The main facets form element
 * @property {HTMLElement | undefined} facetStatus - The facet status element
 *
 * @extends {Component<FacetsFormRefs>}
 */
class FacetsFormComponent extends Component {
  requiredRefs = ['facetsForm'];

  constructor() {
    super();
    this.allFilters = window.allFilterEntries || [];
    this.parentCollection = window.parentCollection || window.currentCollection || "";
    this.activeUrlFilters = [];
    this.isRedirecting = false;
    this.hasAutoChecked = false;
    this.shouldPurgeFiltersOnParent = false;
  }

  connectedCallback() {
    super.connectedCallback();
    
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.attachUrlBasedFilterHandlers();
        this.autoCheckUrlBasedFilters();
        this.stripAllFilterParamsOnParentLoad();
        this.attachHistoryListener();
        // Reinitialize filters when navigating back using BFCache
        window.addEventListener('pageshow', (event) => {
          try {
            const currentPath = window.location.pathname;
            const collectionHandle = currentPath.split("/collections/")[1]?.split("?")[0];
            const parentCollection = this.parentCollection;
            const isOnParent = !collectionHandle || collectionHandle === parentCollection;
            let navType = '';
            try {
              const nav = performance.getEntriesByType('navigation')[0];
              navType = nav && nav.type;
            } catch (e2) {}
            const isBFCache = (/** @type any */(event)).persisted === true || navType === 'back_forward';
            if (isOnParent && isBFCache) {
              this.reinitializeFilters();
            }
          } catch (e3) {}
        });
        this.attachManualTrigger();
        // Inject CSS overrides after a short delay to ensure class is ready
        setTimeout(() => {
          if (this.injectCSSOverrides) {
            this.injectCSSOverrides();
          }
        }, 100);
        
      });
    } else {
      this.attachUrlBasedFilterHandlers();
      this.autoCheckUrlBasedFilters();
      this.stripAllFilterParamsOnParentLoad();
      this.attachHistoryListener();
      // Reinitialize filters when navigating back using BFCache
      window.addEventListener('pageshow', (event) => {
        try {
          const currentPath = window.location.pathname;
          const collectionHandle = currentPath.split("/collections/")[1]?.split("?")[0];
          const parentCollection = this.parentCollection;
          const isOnParent = !collectionHandle || collectionHandle === parentCollection;
          let navType = '';
          try {
            const nav = performance.getEntriesByType('navigation')[0];
            navType = nav && nav.type;
          } catch (e2) {}
          const isBFCache = (/** @type any */(event)).persisted === true || navType === 'back_forward';
          if (isOnParent && isBFCache) {
            this.reinitializeFilters();
          }
        } catch (e3) {}
      });
      this.attachManualTrigger();
      // Inject CSS overrides after a short delay to ensure class is ready
      setTimeout(() => {
        if (this.injectCSSOverrides) {
          this.injectCSSOverrides();
        }
      }, 100);
    }
  }

  /**
   * Fully resets and reinitializes filters on parent after history navigation.
   */
  reinitializeFilters() {
    try {
      this.activeUrlFilters = [];
      this.shouldPurgeFiltersOnParent = false;
      this.stripAllFilterParamsOnParentLoad();
      this.uncheckAllUrlBasedFilters();
      this.forceUncheckAllRedirectFilters();
      this.autoCheckUrlBasedFilters();
      this.updateFilters();
    } catch (e) {}
  }

  /**
   * Strips all filter.* query params on the parent collection to avoid
   * leftover URL-based filters after navigating back from child collections.
   */
  stripAllFilterParamsOnParentLoad() {
    try {
      const currentPath = window.location.pathname;
      const collectionHandle = currentPath.split("/collections/")[1]?.split("?")[0];
      const parentCollection = this.parentCollection;
      const isOnParent = !collectionHandle || collectionHandle === parentCollection;
      if (isOnParent) {
        this.activeUrlFilters = [];
      }
      if (!isOnParent) {
        return;
      }

      const url = new URL(window.location.href);
      const keys = Array.from(url.searchParams.keys());
      const filterKeys = keys.filter((key) => typeof key === 'string' && key.startsWith('filter.'));
      let changed = false;
      if (filterKeys.length) {
        filterKeys.forEach((key) => {
          url.searchParams.delete(key);
          changed = true;
        });
      }

      if (changed) {
        // Clean URL without adding history entries
        history.replaceState({ urlParameters: url.searchParams.toString() }, '', url.toString());
      }
      // Always run DOM cleanup on parent, even if no filter params were present in the URL
      try {
        const formEl = this.refs.facetsForm;
        if (formEl instanceof HTMLFormElement) {
          const hiddenFilterInputs = Array.from(formEl.querySelectorAll('input[type="hidden"][name^="filter."]'));
          if (hiddenFilterInputs.length) {
            hiddenFilterInputs.forEach((input) => input.remove());
          }

          // Uncheck ALL filter checkboxes to clear any BFCache/restored state
          const filterCheckboxes = Array.from(formEl.querySelectorAll('input[type="checkbox"][name^="filter."]'));
          if (filterCheckboxes.length) {
            filterCheckboxes.forEach((cb) => {
              if (!(cb instanceof HTMLInputElement)) return;
              cb.checked = false;
              cb.removeAttribute('checked');
              cb.removeAttribute('data-url-based-filter');
              cb.removeAttribute('data-hidden-from-native');
              cb.removeAttribute('data-force-unchecked');
              const label = cb.closest('label') || document.querySelector(`label[for="${cb.id}"]`);
              if (label) label.classList.remove('active', 'checked');
              const wrapper = cb.closest('.checkbox');
              if (wrapper) wrapper.classList.remove('active', 'checked');
            });
          }

          // Remove any ghost URL-based checkboxes
          const ghostCheckboxes = Array.from(
            formEl.querySelectorAll(
              'input[type="checkbox"][data-url-based-filter], input[type="checkbox"][data-hidden-from-native], input[type="checkbox"][data-force-unchecked]'
            )
          );
          if (ghostCheckboxes.length) {
            ghostCheckboxes.forEach((checkbox) => {
              if (!(checkbox instanceof HTMLInputElement)) return;
              checkbox.checked = false;
              checkbox.removeAttribute('checked');
              checkbox.removeAttribute('data-url-based-filter');
              checkbox.removeAttribute('data-hidden-from-native');
              checkbox.removeAttribute('data-force-unchecked');
              checkbox.removeAttribute('data-original-name');
              checkbox.removeAttribute('name');
              checkbox.style.display = 'none';
              const label = checkbox.closest('label') || document.querySelector(`label[for="${checkbox.id}"]`);
              if (label) label.classList.remove('active', 'checked');
              const wrapper = checkbox.closest('.checkbox');
              if (wrapper) wrapper.classList.remove('active', 'checked');
            });
          }
        }
      } catch (e) {}
      this.uncheckAllUrlBasedFilters();
      // Do not purge on next click; we've already cleaned URL/state on load
      this.shouldPurgeFiltersOnParent = false;
    } catch (e) {}
  }

  /**
   * Attaches a listener for browser back/forward button navigation
   */
  attachHistoryListener() {
    let lastUrl = window.location.pathname;
    
    // Function to check and update checkbox states
    const updateCheckboxStates = () => {
      const currentUrl = window.location.pathname;
      
      if (currentUrl !== lastUrl) {
        lastUrl = currentUrl;
        this.autoCheckUrlBasedFilters();
      }
    };   
      
      updateCheckboxStates();
      setTimeout(updateCheckboxStates, 50);
      setTimeout(updateCheckboxStates, 200); // Extra check to ensure state is correct
  }

  /**
   * Attaches a manual trigger for testing checkbox state updates
   */
  attachManualTrigger() {
    // Add a global function for manual testing
    window.forceUpdateCheckboxStates = () => {
      this.autoCheckUrlBasedFilters();
    };
    
    // Add a global function to inspect checkbox visual state
    window.inspectCheckboxState = () => {
      const allFilterCheckboxes = document.querySelectorAll('input[type="checkbox"][name*="filter"]');
      
      allFilterCheckboxes.forEach((checkbox, index) => {
        if (checkbox instanceof HTMLInputElement) {
          const label = checkbox.closest('label') || document.querySelector(`label[for="${checkbox.id}"]`);
          const checkboxWrapper = checkbox.closest('.checkbox');
          const checkmarkIcon = label?.querySelector('.icon-checkmark');
          
          // Get computed styles to see what CSS is actually applied
          const checkboxComputed = window.getComputedStyle(checkbox);
          const labelComputed = label ? window.getComputedStyle(label) : null;
          const checkmarkComputed = checkmarkIcon ? window.getComputedStyle(checkmarkIcon) : null;
        }
      });
    };

    // Add a global function to force uncheck a specific checkbox
    window.forceUncheckCheckbox = (checkboxName, checkboxValue) => {
      const checkbox = document.querySelector(`input[name="${checkboxName}"][value="${checkboxValue}"]`);
      if (checkbox instanceof HTMLInputElement) {
        
        // Uncheck the checkbox
        checkbox.checked = false;
        checkbox.indeterminate = false;
        checkbox.removeAttribute('checked');
        checkbox.removeAttribute('data-url-based-filter');
        checkbox.removeAttribute('data-hidden-from-native');
        
        // Remove classes from label and wrapper
        const label = checkbox.closest('label') || document.querySelector(`label[for="${checkbox.id}"]`);
        const checkboxWrapper = checkbox.closest('.checkbox');
        
        if (label) {
          label.classList.remove('active', 'checked');
        }
        
        if (checkboxWrapper) {
          checkboxWrapper.classList.remove('active', 'checked');
        }
        
        // Don't hide the checkmark icon - let CSS handle it
        
        // Trigger multiple events to ensure CSS updates
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        checkbox.dispatchEvent(new Event('input', { bubbles: true }));
        checkbox.dispatchEvent(new Event('click', { bubbles: true }));
        
        // Force a reflow to ensure CSS updates
        checkbox.offsetHeight;
      }
        };
    
    // Add a global function to restore all checkboxes to visible state
    window.restoreAllCheckboxes = () => {
      
      const allFilterCheckboxes = document.querySelectorAll('input[type="checkbox"][name*="filter"], input[type="checkbox"]');
      
      allFilterCheckboxes.forEach(checkbox => {
        if (checkbox instanceof HTMLInputElement) {
          // Ensure checkbox is visible
          checkbox.style.display = '';
          checkbox.style.visibility = '';
          checkbox.style.opacity = '';
          
          // Ensure checkbox has a name attribute
          if (!checkbox.hasAttribute('name')) {
            const value = checkbox.value;
            if (value && value.includes('Vendor')) {
              checkbox.setAttribute('name', 'filter.p.vendor');
            } else if (value && value.includes('stock')) {
              checkbox.setAttribute('name', 'filter.v.availability');
            } else if (value) {
              // Try to reconstruct name from value
              checkbox.setAttribute('name', `filter.p.${value.toLowerCase().replace(/\s+/g, '-')}`);
            }
          }
          
          // Ensure label is visible
          const label = checkbox.closest('label') || document.querySelector(`label[for="${checkbox.id}"]`);
          if (label) {
            label.style.display = '';
            label.style.visibility = '';
            label.style.opacity = '';
          }
          
          // Ensure wrapper is visible
          const wrapper = checkbox.closest('.checkbox');
          if (wrapper) {
            wrapper.style.display = '';
            wrapper.style.visibility = '';
            wrapper.style.opacity = '';
          }
        }
      });
      
    };








    

    

  }

  /**
   * Attaches URL-based filter handlers to filter tags
   */
  attachUrlBasedFilterHandlers() {
    const urlBasedFilterTags = document.querySelectorAll(".js-url-based-filter");

    urlBasedFilterTags.forEach(tag => {
      if (!tag.dataset || tag.dataset.handlerAttached) return;
      
      tag.dataset.handlerAttached = "true";
      tag.addEventListener("click", (e) => {
        e.preventDefault();

        if (this.isRedirecting) {
          return;
        }

        const filterSlug = tag.dataset.filterSlug;
        const currentUrl = new URL(window.location.href);
        const currentParams = new URLSearchParams(currentUrl.search);

        const entry = this.allFilters.find(f => f.slug === filterSlug);
        if (entry) {
          const paramToRemove = this.getFilterParamName(entry);
          if (paramToRemove) {
            currentParams.delete(paramToRemove);
          }
        }

        const preservedParams = currentParams.toString();
        const targetUrl = `/collections/${this.parentCollection}${preservedParams ? '?' + preservedParams : ''}`;

        this.navigateToCollection(targetUrl);
      });
    });
  }

  /**
   * Automatically checks URL-based filters based on current URL
   */
  autoCheckUrlBasedFilters() {
    // Track how many times this method is called
    this.callCount = (this.callCount || 0) + 1;
    
    // Prevent multiple rapid calls
    if (this.isProcessingUrlFilters) {
      return;
    }
    
    this.isProcessingUrlFilters = true;
    
    const currentPath = window.location.pathname;
    const collectionHandle = currentPath.split("/collections/")[1]?.split("?")[0];
    const parentCollection = this.parentCollection;



    // If we're on the parent collection, ensure all checkboxes are unchecked
    if (!collectionHandle || collectionHandle === parentCollection) {
      // First, uncheck any URL-based filters (which may not have a name attribute)
      // to ensure child filter state is cleared when returning to the parent collection
      this.uncheckAllUrlBasedFilters();
      const allFilterCheckboxes = document.querySelectorAll('input[type="checkbox"][name*="filter"]');
      
      allFilterCheckboxes.forEach(checkbox => {
        if (checkbox instanceof HTMLInputElement && checkbox.checked) {
          // Check if this is a redirect filter
          const isRedirectFilter = this.isRedirectFilter(checkbox);
          

          
          checkbox.checked = false;
          checkbox.removeAttribute('checked');
          checkbox.removeAttribute('data-url-based-filter');
          checkbox.removeAttribute('data-hidden-from-native');
          
          const label = checkbox.closest('label') || document.querySelector(`label[for="${checkbox.id}"]`);
          const checkboxWrapper = checkbox.closest('.checkbox');
          
          if (label) {
            label.classList.remove('active', 'checked');
          }
          
          if (checkboxWrapper) {
            checkboxWrapper.classList.remove('active', 'checked');
          }
          
          checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      
      // Force uncheck specific checkboxes that might persist
      // Force uncheck all redirect filters when on parent collection
      if (!collectionHandle || collectionHandle === parentCollection) {
        
        // Clear the activeUrlFilters array to prevent filter state confusion
        if (this.activeUrlFilters && this.activeUrlFilters.length > 0) {
          this.activeUrlFilters = [];
        }
        
        // Also clear the faceted-nav.js activeUrlFilters array
        const facetedNavForms = document.querySelectorAll('facet-filters-form');
        facetedNavForms.forEach(form => {
          if (form.activeUrlFilters && form.activeUrlFilters.length > 0) {
            form.activeUrlFilters = [];
          }
        });
        
        // Immediately uncheck redirect filters without delay to prevent flickering
        const allCheckboxes = document.querySelectorAll('input[type="checkbox"][name*="filter"]');
        
        allCheckboxes.forEach(checkbox => {
          if (!(checkbox instanceof HTMLInputElement)) return;
          
          // Check if this is a redirect filter (vendor filter)
          const inputName = checkbox.name || '';
          
          // Simple check: if it's a vendor filter, treat it as redirect
          if (inputName.includes('filter.p.vendor')) {
            
            // Then uncheck the checkbox
            checkbox.checked = false;
            checkbox.removeAttribute('checked');
            checkbox.removeAttribute('data-url-based-filter');
            checkbox.removeAttribute('data-hidden-from-native');
            
            // Remove active classes from label
            const label = checkbox.closest('label') || document.querySelector(`label[for="${checkbox.id}"]`);
            if (label) {
              label.classList.remove('active', 'checked');
            }
            
            // Remove active classes from wrapper elements
            const checkboxWrapper = checkbox.closest('.checkbox');
            if (checkboxWrapper) {
              checkboxWrapper.classList.remove('active', 'checked');
            }
          }
        });
      }
      
      this.isProcessingUrlFilters = false;
      return;
    }

    let fragments = [];

    // Handle both prefix and suffix patterns for sub-level filters
    if (collectionHandle.startsWith(parentCollection + "-")) {
      const suffix = collectionHandle.slice(parentCollection.length + 1);
      fragments = [suffix];
    } else if (collectionHandle.endsWith("-" + parentCollection)) {
      const prefix = collectionHandle.slice(0, collectionHandle.length - parentCollection.length - 1);
      fragments = [prefix];
    } else {
      // Handle complex patterns like "in-stock-hydrogen-vendor"
      const parts = collectionHandle.split("-");
      
      const parentIndex = parts.indexOf(parentCollection);
      if (parentIndex !== -1) {
        // Extract all parts except the parent collection
        fragments = parts.filter((part, index) => index !== parentIndex);
      } else {
        // If parent collection not found, treat all parts as potential filters
        // This handles cases where the parent collection name might not be in the URL
        fragments = parts.filter(part => part !== parentCollection);
      }
    }


    // Try multiple selectors to find checkboxes - prioritize data-filter-key but also include name-based ones
    let allCheckboxes = document.querySelectorAll('input[data-filter-key], input[type="checkbox"][name*="filter"]');

    // If still no checkboxes, try all checkboxes in facet forms
    if (allCheckboxes.length === 0) {
      allCheckboxes = document.querySelectorAll('facets-form-component input[type="checkbox"], facet-filters-form input[type="checkbox"]');
    }

    allCheckboxes.forEach((checkbox, index) => {
      if (!(checkbox instanceof HTMLInputElement)) return;
      // Don't skip checkboxes that don't have dataset - we can still process them using name/value
      
      let filterKey = checkbox.dataset.filterKey;
      let filterValues = checkbox.dataset.filterValues;
      let filterType = '';
      let filterValue = '';

      // If we have the proper data attributes, use them
      if (filterValues) {
      const firstHyphenIndex = filterValues.indexOf('-');
        if (firstHyphenIndex !== -1) {
          filterType = filterValues.substring(0, firstHyphenIndex);
          filterValue = filterValues.substring(firstHyphenIndex + 1);
        }
      } else {
        // Fallback: try to extract from name and value attributes
        const inputName = checkbox.name || '';
        const inputValue = checkbox.value || '';

        // Determine filter type from name
        if (inputName.includes('filter.p.vendor')) {
          filterType = 'vendor';
        } else if (inputName.includes('filter.v.availability')) {
          filterType = 'availability';
        } else if (inputName.includes('filter.p.m.')) {
          filterType = 'metafield';
        } else {
          filterType = 'unknown';
        }

        filterValue = inputValue;
      }

      // Handle availability filter specially
      let filterValueHandle = filterValue.toLowerCase().replace(/\s+/g, '-');
      
      // Special handling for availability filters
      if (filterType === 'availability' || filterType === 'Availability') {
        if (filterValue.toLowerCase().includes('in stock') || filterValue === '1') {
          filterValueHandle = 'in-stock';
        } else if (filterValue.toLowerCase().includes('out of stock') || filterValue === '0') {
          filterValueHandle = 'out-of-stock';
        }
      }

      const shouldCheck = fragments.some(fragment => {
        const matches = fragment === filterValueHandle;
        return matches;
      });

      if (shouldCheck) {
        
        checkbox.checked = true;
        
        checkbox.setAttribute('checked', 'checked');

        
        // Store original name before removing it for URL-based filters
        if (checkbox.hasAttribute('name')) {
          const originalName = checkbox.getAttribute('name');
          if (originalName) {
            checkbox.setAttribute('data-original-name', originalName);
        checkbox.removeAttribute('name');
          }
        }
        try {
        checkbox.setAttribute('data-hidden-from-native', 'true');
        } catch (e) {
        }
        try {
        checkbox.setAttribute('data-url-based-filter', 'true');
        } catch (e) {
        }
        try {
          checkbox.setAttribute('data-just-processed', 'true');
        } catch (e) {
        }
        
        // Also set a timestamp to make it more persistent
        checkbox.setAttribute('data-processed-time', Date.now().toString());
        
        checkbox.setAttribute('data-test-attribute', 'test-value');
        
        // Try to query the element from the DOM to see if it's the same
        const domElement = document.getElementById(checkbox.id);

        // Find the label - try multiple approaches
        let label = checkbox.closest('label');
        if (!label && checkbox.id) {
          label = document.querySelector(`label[for="${checkbox.id}"]`);
        }
        if (!label) {
          // Try finding label as sibling or parent
          label = checkbox.parentElement?.querySelector('label') || 
                  checkbox.parentElement?.closest('label');
        }
        
        if (label) {
          label.classList.add('active');
        }
        
        // Trigger change event to update CSS and any listeners
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        checkbox.dispatchEvent(new Event('input', { bubbles: true }));

        // Only add to activeUrlFilters if we have proper data
        if (filterKey) {
        const entry = this.allFilters.find(f => f.slug === filterKey);
        if (entry) {
          const existingFilter = this.activeUrlFilters.find(f => f.slug === entry.slug);
          if (!existingFilter) {
            this.activeUrlFilters.push({
              slug: entry.slug,
              label: entry.label,
              filterSlug: filterKey,
              filterEntry: entry
            });
            }
          }
        }
      }
    });
    
    // Clean up processing markers after a delay to prevent interference
    setTimeout(() => {
      const processedCheckboxes = document.querySelectorAll('input[data-just-processed]');
      processedCheckboxes.forEach(checkbox => {
        checkbox.removeAttribute('data-just-processed');
        checkbox.removeAttribute('data-processed-time');
      });
    }, 1000);
    
    // Reset the processing flag
    this.isProcessingUrlFilters = false;
  }

  /**
   * Unchecks all URL-based filter checkboxes
   */
  uncheckAllUrlBasedFilters() {
    const allCheckboxes = document.querySelectorAll('input[type="checkbox"][name*="filter"], facets-form-component input[type="checkbox"]');
    
    allCheckboxes.forEach(checkbox => {
      if (!(checkbox instanceof HTMLInputElement)) return;
      
      // Skip checkboxes that were just processed (with timestamp check)
      if (checkbox.hasAttribute('data-just-processed') || checkbox.hasAttribute('data-processed-time')) {
        return;
      }
      
      // Check if this checkbox has URL-based filter attributes
      const hasUrlBasedFilter = checkbox.hasAttribute('data-url-based-filter') || 
                               checkbox.hasAttribute('data-hidden-from-native');
      
      if (hasUrlBasedFilter) {
        checkbox.checked = false;
        checkbox.removeAttribute('checked');
        checkbox.removeAttribute('data-url-based-filter');
        checkbox.removeAttribute('data-hidden-from-native');
        // Restore original name if it was removed for URL-based filters
        const originalName = checkbox.getAttribute('data-original-name');
        if (originalName && !checkbox.hasAttribute('name')) {
          checkbox.setAttribute('name', originalName);
          checkbox.removeAttribute('data-original-name');
        }
        
        // Remove active class from label
        const label = checkbox.closest('label');
        if (label) {
          label.classList.remove('active');
        }
      }
    });
  }

  /**
   * Navigates to a collection URL
   * @param {string} targetUrl - The target URL to navigate to
   */
  navigateToCollection(targetUrl) {
    this.isRedirecting = true;

    setTimeout(() => {
      this.isRedirecting = false;
    }, 2000);

    window.location.href = targetUrl;
  }

  /**
   * Gets the filter parameter name for a given filter entry
   * @param {Object} entry - The filter entry object
   * @returns {string|null} The parameter name or null
   */
  getFilterParamName(entry) {
    if (entry && /** @type {any} */ (entry).handle === 'brand') {
      return 'filter.p.vendor';
    } else if (entry && (/** @type {any} */ (entry).filter_handle === 'availability' || /** @type {any} */ (entry).handle_fragment === 'availability')) {
      return 'filter.v.availability';
    } else if (entry && /** @type {any} */ (entry).metafield_namespace && /** @type {any} */ (entry).filter_handle) {
      return `filter.p.m.${/** @type {any} */ (entry).metafield_namespace}.${/** @type {any} */ (entry).filter_handle}`;
    }
    return null;
  }

  /**
   * Gets the filter value for URL construction
   * @param {Object} entry - The filter entry object
   * @param {string} filterValue - The original filter value
   * @returns {string} The processed filter value
   */
  getFilterValueForUrl(entry, filterValue) {
    // Special handling for availability filters
    if (entry && (/** @type {any} */ (entry).filter_handle === 'availability' || /** @type {any} */ (entry).handle_fragment === 'availability')) {
      if (filterValue.toLowerCase().includes('in stock') || filterValue === '1') {
        return 'in-stock';
      } else if (filterValue.toLowerCase().includes('out of stock') || filterValue === '0') {
        return 'out-of-stock';
      }
    }
    
    // Default: convert to URL-friendly format
    return filterValue.toLowerCase().replace(/\s+/g, '-');
  }

  /**
   * Gets the filter label for display
   * @param {Object} entry - The filter entry object
   * @returns {string} The filter label
   */
  getFilterLabel(entry) {
    if (!entry) return 'Filter';

    if (/** @type {any} */ (entry).handle === 'brand' || /** @type {any} */ (entry).filter_handle === 'brand') {
      return 'Brand';
    } else if (/** @type {any} */ (entry).filter_handle === 'availability') {
      return 'Availability';
    } else if (/** @type {any} */ (entry).handle) {
      return /** @type {any} */ (entry).handle.charAt(0).toUpperCase() + /** @type {any} */ (entry).handle.slice(1).replace(/-/g, ' ');
    }
    return 'Filter';
  }

  /**
   * Creates URL parameters from form data
   * @param {FormData} [formData] - Optional form data to use instead of the main form
   * @returns {URLSearchParams} The processed URL parameters
   */
  createURLParameters(formData = new FormData(this.refs.facetsForm)) {
    // Sanitize formData: only include filter.* from REAL checked checkboxes (not URL-based/ghost)
    // Allow price inputs (gte/lte) even if not checkboxes
    const sanitized = new FormData();
    try {
      const formEl = this.refs.facetsForm;
      for (const [key, value] of formData.entries()) {
        const isFilterKey = typeof key === 'string' && key.startsWith('filter.');
        if (!isFilterKey) {
          sanitized.append(key, value);
          continue;
        }
        const inputs = formEl instanceof HTMLFormElement
          ? Array.from(formEl.querySelectorAll(`[name="${key}"]`))
          : [];
        const hasRealCheckedCheckbox = inputs.some((el) => {
          return el instanceof HTMLInputElement &&
                 el.type === 'checkbox' &&
                 el.checked &&
                 !el.hasAttribute('data-url-based-filter') &&
                 !el.hasAttribute('data-hidden-from-native') &&
                 !el.hasAttribute('data-force-unchecked');
        });
        const isPriceKey = key === 'filter.v.price.gte' || key === 'filter.v.price.lte';
        if (hasRealCheckedCheckbox || isPriceKey) {
          // For price, skip empty strings (handled later too)
          if (isPriceKey && (value === '' || value === null)) continue;
          sanitized.append(key, value);
        }
      }
    } catch (e) {}

    let newParameters = new URLSearchParams(/** @type any */ (sanitized));
    try {
      const filterParams = Array.from(newParameters.entries())
        .filter(([key]) => typeof key === 'string' && key.startsWith('filter.'))
        .map(([key, value]) => ({ key, value }));
      const activeFilters = Array.isArray(this.activeUrlFilters)
        ? this.activeUrlFilters.map((f) => ({ slug: /** @type any */ (f).slug, label: /** @type any */ (f).label }))
        : [];
    } catch (e) {}

    // Ensure any URL-based (redirect) filters are not serialized when on parent
    // or when flagged on the input as URL-based/force-unchecked
    try {
      const currentPath = window.location.pathname;
      const collectionHandle = currentPath.split("/collections/")[1]?.split("?")[0];
      const parentCollection = this.parentCollection;
      const isOnParent = !collectionHandle || collectionHandle === parentCollection;

      // Collect keys from inputs flagged as URL-based
      const flaggedInputs = Array.from(document.querySelectorAll('input[type="checkbox"][data-url-based-filter], input[type="checkbox"][data-hidden-from-native], input[type="checkbox"][data-force-unchecked]'));
      const flaggedKeys = new Set(
        flaggedInputs
          .map((cb) => (cb instanceof HTMLInputElement ? cb.getAttribute('name') || cb.getAttribute('data-original-name') : null))
          .filter((k) => typeof k === 'string' && k && k.startsWith('filter.'))
      );

      // Collect keys from activeUrlFilters (URL-based filters derived from child URL)
      const urlBasedKeysFromState = new Set();
      if (Array.isArray(this.activeUrlFilters)) {
        this.activeUrlFilters.forEach((f) => {
          const entry = /** @type any */ (f).filterEntry;
          if (entry) {
            const key = this.getFilterParamName(entry);
            if (key && key.startsWith('filter.')) urlBasedKeysFromState.add(key);
          }
        });
      }

      const keysToDelete = new Set([...Array.from(flaggedKeys), ...Array.from(urlBasedKeysFromState)]);

      const removedKeys = [];
      keysToDelete.forEach((key) => {
        if (!key) return;
        if (flaggedKeys.has(key) || isOnParent) {
          if (newParameters.has(key)) {
            newParameters.delete(key);
            removedKeys.push(key);
          }
        }
      });
    } catch (e) {}

    if (newParameters.get('filter.v.price.gte') === '') newParameters.delete('filter.v.price.gte');
    if (newParameters.get('filter.v.price.lte') === '') newParameters.delete('filter.v.price.lte');

    newParameters.delete('page');

    const searchQuery = this.#getSearchQuery();
    if (searchQuery) newParameters.set(SEARCH_QUERY, searchQuery);

    // Final safeguard: remove any filter params that don't have a corresponding
    // real checked checkbox (i.e., only hidden/url-based remnants)
    try {
      const formEl = this.refs.facetsForm;
      const keys = Array.from(newParameters.keys()).filter((k) => typeof k === 'string' && k.startsWith('filter.'));
const actuallyRemoved = [];

keys.forEach((key) => {
  const inputs = Array.from(formEl.querySelectorAll(`[name="${key}"], [data-original-name="${key}"]`));
  const hasRealChecked = inputs.some((el) => {
    return el instanceof HTMLInputElement &&
           el.checked &&
           !el.hasAttribute('data-url-based-filter') &&
           !el.hasAttribute('data-hidden-from-native') &&
           !el.hasAttribute('data-force-unchecked');
  });

  const isPriceKey = key === 'filter.v.price.gte' || key === 'filter.v.price.lte';
  if (!hasRealChecked && !isPriceKey) {
    if (newParameters.has(key)) {
      newParameters.delete(key);
      actuallyRemoved.push(key);
    }
  }
});
      if (formEl instanceof HTMLFormElement) {
        const keys = Array.from(newParameters.keys()).filter((k) => typeof k === 'string' && k.startsWith('filter.'));
        const actuallyRemoved = [];
        keys.forEach((key) => {
          const inputs = Array.from(formEl.querySelectorAll(`[name="${key}"]`));
          const hasRealChecked = inputs.some((el) => {
            if (!(el instanceof HTMLInputElement)) return false;
            if (el.type !== 'checkbox') return false;
            const isUrlBased = el.hasAttribute('data-url-based-filter') || el.hasAttribute('data-hidden-from-native') || el.hasAttribute('data-force-unchecked');
            return el.checked && !isUrlBased;
          });
          const isPriceKey = key === 'filter.v.price.gte' || key === 'filter.v.price.lte';
          if (!hasRealChecked && !isPriceKey) {
            if (newParameters.has(key)) {
              newParameters.delete(key);
              actuallyRemoved.push(key);
            }
          }
        });
      }
    } catch (e) {}

    return newParameters;
  }

  /**
   * Gets the search query parameter from the current URL
   * @returns {string} The search query
   */
  #getSearchQuery() {
    const url = new URL(window.location.href);
    return url.searchParams.get(SEARCH_QUERY) ?? '';
  }

  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('Section ID is required');
    return id;
  }

  /**
   * Updates the URL hash with current filter parameters
   */
  #updateURLHash() {
    const url = new URL(window.location.href);
    const urlParameters = this.createURLParameters();

    // Hard purge: if on parent collection, remove ALL filter.* params, but only once after landing
    try {
      const currentPath = window.location.pathname;
      const collectionHandle = currentPath.split("/collections/")[1]?.split("?")[0];
      const parentCollection = this.parentCollection;
      const isOnParent = !collectionHandle || collectionHandle === parentCollection;
      if (isOnParent && this.shouldPurgeFiltersOnParent) {
        const keys = Array.from(urlParameters.keys());
        const removed = [];
        keys.forEach((key) => {
          if (typeof key === 'string' && key.startsWith('filter.')) {
            urlParameters.delete(key);
            removed.push(key);
          }
        });
        // Disable further purging so normal filters can be added
        this.shouldPurgeFiltersOnParent = false;
      }
    } catch (e) {}

    // As a final guard, on the parent collection, strip any filter.* params
    // that don't correspond to a real, non-URL-based checked checkbox in the form
    try {
      const currentPath = window.location.pathname;
      const collectionHandle = currentPath.split("/collections/")[1]?.split("?")[0];
      const parentCollection = this.parentCollection;
      const isOnParent = !collectionHandle || collectionHandle === parentCollection;
      if (isOnParent && this.refs.facetsForm instanceof HTMLFormElement) {
        const formEl = this.refs.facetsForm;
        const checkedReal = Array.from(formEl.querySelectorAll('input[type="checkbox"][name^="filter."]:checked'))
          .filter((el) => el instanceof HTMLInputElement)
          .filter((el) => !el.hasAttribute('data-url-based-filter') && !el.hasAttribute('data-hidden-from-native') && !el.hasAttribute('data-force-unchecked'))
          .map((el) => ({ name: el.getAttribute('name') || '', value: el.value }));

        const allowedPairs = new Set(checkedReal.map(({ name, value }) => `${name}\u0000${value}`));
        const before = Array.from(urlParameters.entries()).filter(([k]) => k.startsWith('filter.'));
        Array.from(urlParameters.entries()).forEach(([key, value]) => {
          if (!key.startsWith('filter.')) return;
          // Skip price keys - they are text inputs, not checkboxes
          const isPriceKey = key === 'filter.v.price.gte' || key === 'filter.v.price.lte';
          if (isPriceKey) return;
          const pairKey = `${key}\u0000${value}`;
          if (!allowedPairs.has(pairKey)) {
            urlParameters.delete(key);
          }
        });
        const after = Array.from(urlParameters.entries()).filter(([k]) => k.startsWith('filter.'));
      }
    } catch (e) {}

    url.search = '';
    for (const [param, value] of urlParameters.entries()) {
      url.searchParams.append(param, value);
    }

    history.pushState({ urlParameters: urlParameters.toString() }, '', url.toString());
  }

  /**
   * Updates filters and renders the section
   */
  updateFilters = () => {
    this.#updateURLHash();
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  };

  /**
   * Updates the section
   */
  #updateSection() {
    const viewTransition = !this.closest('dialog');

    if (viewTransition) {
      startViewTransition(() => sectionRenderer.renderSection(this.sectionId), ['product-grid']);
    } else {
      sectionRenderer.renderSection(this.sectionId);
    }
  }

  /**
   * Updates filters based on a provided URL
   * @param {string} url - The URL to update filters with
   */
  updateFiltersByURL(url) {
    history.pushState('', '', url);
    this.dispatchEvent(new FilterUpdateEvent(this.createURLParameters()));
    this.#updateSection();
  }

  /**
   * Checks if a collection exists before creating a link
   * @param {string} collectionHandle - The collection handle to check
   * @returns {boolean} True if collection exists, false otherwise
   */
  async checkCollectionExists(collectionHandle) {
    try {
      // Try to fetch the collection page to see if it exists
      const response = await fetch(`/collections/${collectionHandle}`, {
        method: 'HEAD',
        headers: {
          'Accept': 'text/html'
        }
      });
      return response.ok;
    } catch (error) {
      console.warn(`Failed to check if collection ${collectionHandle} exists:`, error);
      return false;
    }
  }

  /**
   * Validates and creates collection links with existence checking
   * @param {string} baseCollection - The base collection handle
   * @param {string} filterFragment - The filter fragment to append
   * @returns {Promise<string|null>} The validated URL or null if collection doesn't exist
   */
  async createValidatedCollectionUrl(baseCollection, filterFragment) {
    const testHandle1 = `${baseCollection}-${filterFragment}`;
    const testHandle2 = `${filterFragment}-${baseCollection}`;
    
    // Check both possible collection handle patterns
    const exists1 = await this.checkCollectionExists(testHandle1);
    if (exists1) {
      return `/collections/${testHandle1}`;
    }
    
    const exists2 = await this.checkCollectionExists(testHandle2);
    if (exists2) {
      return `/collections/${testHandle2}`;
    }
    
    return null;
  }

  /**
   * Processes filter values for URL construction
   * @param {string} filterType - The filter type
   * @param {string} filterValue - The filter value
   * @returns {string} The processed filter value
   */
  processFilterValueForUrl(filterType, filterValue) {
    // Special handling for availability filters
    if (filterType === 'availability' || filterType === 'Availability') {
      if (filterValue.toLowerCase().includes('in stock') || filterValue === '1') {
        return 'in-stock';
      } else if (filterValue.toLowerCase().includes('out of stock') || filterValue === '0') {
        return 'out-of-stock';
      }
    }
    
    // Default: convert to URL-friendly format
    return filterValue.toLowerCase().replace(/\s+/g, '-');
  }
}

if (!customElements.get('facets-form-component')) {
  customElements.define('facets-form-component', FacetsFormComponent);
}

// Debug utility for filter redirects
window.debugFilterRedirects = function() {
  const facetInputs = document.querySelectorAll('facet-inputs-component input[type="checkbox"]');
  const currentPath = window.location.pathname;
  const currentCollection = currentPath.split("/collections/")[1]?.split("?")[0];
  
  
  return 'Check console for filter redirect debug information.';
};

// Debug utility for URL parsing
window.debugUrlParsing = function() {
  const currentPath = window.location.pathname;
  const collectionHandle = currentPath.split("/collections/")[1]?.split("?")[0];
  const parentCollection = window.parentCollection;
  
  if (!collectionHandle || collectionHandle === parentCollection) {
    return;
  }
  
  let fragments = [];
  
  if (collectionHandle.startsWith(parentCollection + "-")) {
    const suffix = collectionHandle.slice(parentCollection.length + 1);
    fragments = [suffix];
  } else if (collectionHandle.endsWith("-" + parentCollection)) {
    const prefix = collectionHandle.slice(0, collectionHandle.length - parentCollection.length - 1);
    fragments = [prefix];
  } else {
    const parts = collectionHandle.split("-");
    
    const parentIndex = parts.indexOf(parentCollection);
    if (parentIndex !== -1) {
      fragments = parts.filter((part, index) => index !== parentIndex);
    } else {
      fragments = parts.filter(part => part !== parentCollection);
    }
  }
  
  return 'Check console for URL parsing debug information.';
};

/**
 * @typedef {Object} FacetInputsRefs
 * @property {HTMLInputElement[]} facetInputs - The facet input elements
 */

/**
 * Handles individual facet input functionality
 * @extends {Component<FacetInputsRefs>}
 */
class FacetInputsComponent extends Component {
  get sectionId() {
    const id = this.closest('.shopify-section')?.id;
    if (!id) throw new Error('FacetInputs component must be a child of a section');
    return id;
  }

  /**
   * Updates filters and the selected facet summary
   */
  async updateFilters() {
    const facetsForm = this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    // Check if this filter change should trigger a collection redirect
    const shouldRedirect = await this.#shouldRedirectToCollection();
    if (shouldRedirect) {
      return; // Redirect handled, don't proceed with normal filtering
    }

    facetsForm.updateFilters();
    this.#updateSelectedFacetSummary();
  }



  /**
   * Handles keydown events for the facets form
   * @param {KeyboardEvent} event - The keydown event
   */
  handleKeyDown(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const closestInput = event.target.querySelector('input');

    if (!(closestInput instanceof HTMLInputElement)) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      closestInput.checked = !closestInput.checked;
      this.updateFilters().catch(error => {
        console.error('Error updating filters:', error);
      });
    }
  }

  /**
   * Handles mouseover events on facet labels
   * @param {MouseEvent} event - The mouseover event
   */
  prefetchPage = debounce((event) => {
    if (!(event.target instanceof HTMLElement)) return;

    const form = this.closest('form');
    if (!form) return;

    const formData = new FormData(form);
    const inputElement = event.target.querySelector('input');

    if (!(inputElement instanceof HTMLInputElement)) return;

    if (!inputElement.checked) formData.append(inputElement.name, inputElement.value);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    const urlParameters = facetsForm.createURLParameters(formData);

    const url = new URL(window.location.pathname, window.location.origin);

    for (const [key, value] of urlParameters) url.searchParams.append(key, value);

    if (inputElement.checked) url.searchParams.delete(inputElement.name, inputElement.value);

    sectionRenderer.getSectionHTML(this.sectionId, true, url);
  }, 200);

  cancelPrefetchPage = () => this.prefetchPage.cancel();

  /**
   * Checks if the current filter change should trigger a collection redirect
   * @returns {Promise<boolean>} True if a redirect was performed, false otherwise
   */
  async #shouldRedirectToCollection() {
    
    // Find any recently changed checkbox that hasn't been processed yet
    let recentlyChangedInput = this.refs.facetInputs?.find(input => {
      if (!(input instanceof HTMLInputElement)) return false;
      if (input.hasAttribute('data-redirect-checked')) return false;
      

      
      // Look for checked checkboxes that are filter inputs
      const isChecked = input.checked;
      const isFilterInput = input.name && input.name.includes('filter');
      
      return isChecked && isFilterInput;
    });

    // Special case: if no checked checkbox found, check if any checkbox with data-force-unchecked was clicked
    if (!recentlyChangedInput) {
      const forceUncheckedInput = this.refs.facetInputs?.find(input => {
        if (!(input instanceof HTMLInputElement)) return false;
        if (input.hasAttribute('data-redirect-checked')) return false;
        
        // Look for force unchecked checkboxes that are filter inputs
        const hasForceUnchecked = input.hasAttribute('data-force-unchecked');
        const isFilterInput = input.name && input.name.includes('filter');
        
        return hasForceUnchecked && isFilterInput;
      });
      
      if (forceUncheckedInput) {
        recentlyChangedInput = forceUncheckedInput;
        // Mark it as unchecked for the redirect logic
        recentlyChangedInput.checked = false;
      } else {
        // Special case: if we're on a child collection, look for any checkbox that might represent the current collection
        const currentCollectionHandle = window.location.pathname.split("/collections/")[1]?.split("?")[0];
        const parentCollection = window.parentCollection || currentCollectionHandle;
        const isOnChildCollection = currentCollectionHandle !== parentCollection;
        
        if (isOnChildCollection) {
          const currentCollectionCheckbox = this.refs.facetInputs?.find(input => {
            if (!(input instanceof HTMLInputElement)) return false;
            if (input.hasAttribute('data-redirect-checked')) return false;
            
            const checkboxValue = input.value;
            // Check if this checkbox value matches the current collection
            if (currentCollectionHandle && checkboxValue && 
                (currentCollectionHandle.includes(checkboxValue.toLowerCase().replace(/\s+/g, '-')) ||
                 checkboxValue.toLowerCase().replace(/\s+/g, '-').includes(currentCollectionHandle))) {
              return true;
            }
            return false;
          });
          
          if (currentCollectionCheckbox) {
            recentlyChangedInput = currentCollectionCheckbox;
          } else {
            return false;
          }
        } else {
          return false;
        }
      }
    }
    
    // Mark this input to avoid duplicate processing
    recentlyChangedInput.setAttribute('data-redirect-checked', 'true');

    // Get current collection information
    const currentPath = window.location.pathname;
    const currentCollectionHandle = currentPath.split("/collections/")[1]?.split("?")[0];
    const parentCollection = window.parentCollection || currentCollectionHandle;

    if (!currentCollectionHandle || !parentCollection) {
      recentlyChangedInput.removeAttribute('data-redirect-checked');
      return false;
    }

    // Check if we're on a child collection (not the parent)
    const isOnChildCollection = currentCollectionHandle !== parentCollection;
    const isCheckboxChecked = recentlyChangedInput.checked;



    // Get filter information
    const filterKey = recentlyChangedInput.dataset.filterKey;
    const filterValues = recentlyChangedInput.dataset.filterValues;
    const filterValue = recentlyChangedInput.value;

    // Extract filter type and value from data attributes
    let filterType = '';
    let filterValueForUrl = '';
    
    if (filterValues) {
      const parts = filterValues.split('-');
      if (parts.length >= 2) {
        filterType = parts[0];
        const originalValue = parts.slice(1).join('-');
        filterValueForUrl = this.processFilterValueForUrl(filterType, originalValue);
      }
    }
    
    // If we couldn't extract from filterValues, try the value attribute
    if (!filterValueForUrl && filterValue) {
      const inputName = recentlyChangedInput.name || '';
      if (inputName.includes('availability')) {
        filterType = 'availability';
      } else if (inputName.includes('vendor')) {
        filterType = 'vendor';
      }
      
      filterValueForUrl = this.processFilterValueForUrl(filterType, filterValue);
    }

    // Check for filter entries data
    const allFilterEntries = window.allFilterEntries || [];
    const filterEntry = allFilterEntries.find(entry => 
      entry.slug === filterKey || 
      entry.handle_fragment === filterValueForUrl ||
      entry.label?.toLowerCase().replace(/\s+/g, '-') === filterValueForUrl
    );

    if (!filterValueForUrl) {
      recentlyChangedInput.removeAttribute('data-redirect-checked');
      return false;
    }

    // Check if we should redirect based on filter type
    const shouldRedirect = this.#shouldFilterTriggerRedirect(filterEntry, filterValueForUrl, currentCollectionHandle);
    
    if (!shouldRedirect) {
      recentlyChangedInput.removeAttribute('data-redirect-checked');
      return false;
    }

    // Handle unchecking on child collections - navigate back to parent
    if (isOnChildCollection && !isCheckboxChecked) {
      const parentUrl = `/collections/${parentCollection}`;
      window.location.href = parentUrl;
      return true;
    }

    // NEW: Handle unchecking when checkbox is currently checked on child collection
    if (isOnChildCollection && isCheckboxChecked) {
      // Check if this checkbox represents the current child collection
      const currentCollectionHandle = window.location.pathname.split("/collections/")[1]?.split("?")[0];
      const checkboxValue = recentlyChangedInput.value;
      
      // If this checkbox represents the current collection, uncheck it and go to parent
      if (currentCollectionHandle && checkboxValue && 
          (currentCollectionHandle.includes(checkboxValue.toLowerCase().replace(/\s+/g, '-')) ||
           checkboxValue.toLowerCase().replace(/\s+/g, '-').includes(currentCollectionHandle))) {
        const parentUrl = `/collections/${parentCollection}`;
        window.location.href = parentUrl;
        return true;
      }
    }

    // Handle checking on parent collections - navigate to child collection
    if (!isOnChildCollection && isCheckboxChecked) {
      // Check if we should skip collection validation for this filter
      const shouldSkipValidation = this.#shouldSkipCollectionValidation(filterEntry, filterValueForUrl);
      
      if (shouldSkipValidation) {
      recentlyChangedInput.removeAttribute('data-redirect-checked');
      return false;
    }

      // Validate collection exists before redirecting
      try {
        const targetUrl = await this.createValidatedCollectionUrl(currentCollectionHandle, filterValueForUrl);
        
        if (targetUrl) {
          // Preserve existing filters when redirecting to child collection
          const preservedUrl = this.preserveExistingFilters(targetUrl);
          window.location.href = preservedUrl;
      return true;
        } else {
          recentlyChangedInput.removeAttribute('data-redirect-checked');
          return false;
        }
    } catch (error) {
      recentlyChangedInput.removeAttribute('data-redirect-checked');
      return false;
    }
    }

    // Proceed with normal filtering
    recentlyChangedInput.removeAttribute('data-redirect-checked');
    return false;
  }

  /**
   * Determines if a filter should trigger a collection redirect
   * @param {Object} filterEntry - The filter entry from allFilterEntries
   * @param {string} filterHandle - The filter handle
   * @param {string} currentCollection - The current collection handle
   * @returns {boolean} True if should redirect, false otherwise
   */
  #shouldFilterTriggerRedirect(filterEntry, filterHandle, currentCollection) {
    
    if (!filterEntry) {
      return false;
    }

    // Check if this is a vendor/brand filter
    if (filterEntry.filter_handle === 'brand' || 
        filterEntry.handle === 'brand' ||
        filterEntry.metafield_namespace === 'custom' ||
        (filterEntry.slug && filterEntry.slug.startsWith('brand-')) ||
        (filterEntry.slug && filterEntry.slug.includes('-vendor'))) {
      return true;
    }
    return false;
  }

  /**
   * Determines if a filter should skip collection validation
   * @param {Object} filterEntry - The filter entry from allFilterEntries
   * @param {string} filterValue - The filter value
   * @returns {boolean} True if should skip validation, false otherwise
   */
  #shouldSkipCollectionValidation(filterEntry, filterValue) {
    // Skip validation for certain filter values that we know should work with normal filtering
    const skipValidationValues = ['multi-managed-vendor', 'multi-managed-vendor'];
    
    if (filterValue && skipValidationValues.includes(filterValue.toLowerCase())) {
      return true;
    }
    
    return false;
  }

  /**
   * Injects CSS rules to override problematic checkbox selectors
   */
  injectCSSOverrides() {
    const styleId = 'checkbox-override-styles';
    if (document.getElementById(styleId)) return; // Already injected
    
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      /* Override checkbox visual states when unchecked */
      input[type="checkbox"]:not([checked]):not(:checked) + label .icon-checkmark,
      input[type="checkbox"]:not([checked]):not(:checked) ~ label .icon-checkmark {
        display: none !important;
        opacity: 0 !important;
        visibility: hidden !important;
      }
      
      /* Override active states for unchecked checkboxes */
      input[type="checkbox"]:not([checked]):not(:checked) ~ .checkbox.active,
      input[type="checkbox"]:not([checked]):not(:checked) + label.active,
      input[type="checkbox"]:not([checked]):not(:checked) ~ label.active {
        background-color: transparent !important;
      }
      
      /* Force unchecked state - override :checked styles */
      input[type="checkbox"][data-force-unchecked] {
        background-color: transparent !important;
      }
      input[type="checkbox"][data-force-unchecked] + label .icon-checkmark,
      input[type="checkbox"][data-force-unchecked] ~ label .icon-checkmark {
        display: none !important;
        opacity: 0 !important;
        visibility: hidden !important;
      }
      
      /* Override :checked styles when data-force-unchecked is present */
      input[type="checkbox"][data-force-unchecked]:checked + label .icon-checkmark,
      input[type="checkbox"][data-force-unchecked]:checked ~ label .icon-checkmark {
        display: none !important;
        opacity: 0 !important;
        visibility: hidden !important;
      }
      
      input[type="checkbox"][data-force-unchecked]:checked + label,
      input[type="checkbox"][data-force-unchecked]:checked ~ label {
        background-color: transparent !important;
      }
      
      input[type="checkbox"][data-force-unchecked]:checked ~ .checkbox.active,
      input[type="checkbox"][data-force-unchecked]:checked + label.active,
      input[type="checkbox"][data-force-unchecked]:checked ~ label.active {
        background-color: transparent !important;
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Force unchecks all redirect filters when navigating back to parent collection
   */
  forceUncheckAllRedirectFilters() {
    
    const allCheckboxes = document.querySelectorAll('input[type="checkbox"][name*="filter"]');
    
    allCheckboxes.forEach(checkbox => {
      if (!(checkbox instanceof HTMLInputElement)) return;
      
      // Check if this is a redirect filter
      if (this.isRedirectFilter(checkbox)) {
        
        // Uncheck the checkbox
        checkbox.checked = false;
        checkbox.removeAttribute('checked');
        checkbox.removeAttribute('data-url-based-filter');
        checkbox.removeAttribute('data-hidden-from-native');
        // Restore original name if present
        const originalName = checkbox.getAttribute('data-original-name');
        if (originalName && !checkbox.hasAttribute('name')) {
          checkbox.setAttribute('name', originalName);
          checkbox.removeAttribute('data-original-name');
        }
        
        // Add force unchecked attribute to trigger CSS overrides
        checkbox.setAttribute('data-force-unchecked', 'true');
        
        // Remove active classes from label
        const label = checkbox.closest('label') || document.querySelector(`label[for="${checkbox.id}"]`);
        if (label) {
          label.classList.remove('active', 'checked');
        }
        
        // Force hide any checkmark icons
        const checkmarkIcon = label?.querySelector('.icon-checkmark');
        if (checkmarkIcon) {
          checkmarkIcon.style.setProperty('display', 'none', 'important');
          checkmarkIcon.style.setProperty('opacity', '0', 'important');
          checkmarkIcon.style.setProperty('visibility', 'hidden', 'important');
        }
        
        // Remove active classes from wrapper elements
        const checkboxWrapper = checkbox.closest('.checkbox');
        if (checkboxWrapper) {
          checkboxWrapper.classList.remove('active', 'checked');
        }
        
        // Force CSS reset with multiple events
        checkbox.dispatchEvent(new Event('change', { bubbles: true }));
        checkbox.dispatchEvent(new Event('input', { bubbles: true }));
        checkbox.dispatchEvent(new Event('click', { bubbles: true }));
        
        // Force a reflow to ensure CSS updates
        checkbox.offsetHeight;
      }
    });
    
  }

  /**
   * Checks if a checkbox is a redirect filter
   * @param {HTMLInputElement} checkbox - The checkbox to check
   * @returns {boolean} True if it's a redirect filter, false otherwise
   */
  isRedirectFilter(checkbox) {
    const filterKey = checkbox.dataset.filterKey;
    const filterValues = checkbox.dataset.filterValues;
    const inputName = checkbox.name || '';
    const inputValue = checkbox.value || '';
    
    // Extract filter type and value
    let filterType = '';
    let filterValue = '';
    
    if (filterValues) {
      const firstHyphenIndex = filterValues.indexOf('-');
      if (firstHyphenIndex !== -1) {
        filterType = filterValues.substring(0, firstHyphenIndex);
        filterValue = filterValues.substring(firstHyphenIndex + 1);
      }
    } else {
      // Fallback: try to extract from name and value attributes
      if (inputName.includes('filter.p.vendor')) {
        filterType = 'vendor';
      } else if (inputName.includes('filter.v.availability')) {
        filterType = 'availability';
      } else if (inputName.includes('filter.p.m.')) {
        filterType = 'metafield';
      } else {
        filterType = 'unknown';
      }
      filterValue = inputValue;
    }
    
    // Check for filter entries data
    const allFilterEntries = window.allFilterEntries || [];
    const filterEntry = allFilterEntries.find(entry => 
      entry.slug === filterKey || 
      entry.handle_fragment === filterValue.toLowerCase().replace(/\s+/g, '-') ||
      entry.label?.toLowerCase().replace(/\s+/g, '-') === filterValue.toLowerCase().replace(/\s+/g, '-')
    );
    // Use the same logic as #shouldFilterTriggerRedirect
    if (!filterEntry) {
      // Special case: if it's a vendor filter by name, treat it as redirect
      if (inputName.includes('filter.p.vendor')) {
      return true;
      }
      return false;
    }
    
    const isRedirect = filterEntry.filter_handle === 'brand' || 
           filterEntry.handle === 'brand' ||
           filterEntry.metafield_namespace === 'custom' ||
           (filterEntry.slug && filterEntry.slug.startsWith('brand-')) ||
           (filterEntry.slug && filterEntry.slug.includes('-vendor'));
           
    return isRedirect;
  }

  /**
   * Preserves existing filters when redirecting to a child collection
   * @param {string} targetUrl - The target collection URL
   * @returns {string} The target URL with existing filters preserved
   */
  preserveExistingFilters(targetUrl) {
    const currentUrl = new URL(window.location.href);
    const targetUrlObj = new URL(targetUrl, window.location.origin);
    
    // Get all current query parameters
    const currentParams = currentUrl.searchParams;
    
    // Find all checked checkboxes that are NOT the redirect filter
    const allCheckboxes = document.querySelectorAll('input[type="checkbox"][name*="filter"]:checked');
    const preservedFilters = new URLSearchParams();
    
    allCheckboxes.forEach(checkbox => {
      if (!(checkbox instanceof HTMLInputElement)) return;
      
      // Skip the checkbox that triggered the redirect (it will be handled by the collection URL)
      if (checkbox.hasAttribute('data-redirect-checked')) {
        return;
      }
      
      // Skip URL-based filters (they're already in the collection URL)
      if (checkbox.hasAttribute('data-url-based-filter') || checkbox.hasAttribute('data-hidden-from-native')) {
        return;
      }
      
      // Preserve this filter
      const filterName = checkbox.name;
      const filterValue = checkbox.value;
      
      if (filterName && filterValue) {
        preservedFilters.append(filterName, filterValue);
      }
    });
    
    // Add preserved filters to target URL
    for (const [key, value] of preservedFilters.entries()) {
      targetUrlObj.searchParams.append(key, value);
    }
    
    // Also preserve any other query parameters (like sort_by, page, etc.)
    for (const [key, value] of currentParams.entries()) {
      if (!key.startsWith('filter.') && !preservedFilters.has(key)) {
        targetUrlObj.searchParams.set(key, value);
      }
    }
    
    return targetUrlObj.toString();
  }

  /**
   * Processes filter values for URL construction
   * @param {string} filterType - The filter type
   * @param {string} filterValue - The filter value
   * @returns {string} The processed filter value
   */
  processFilterValueForUrl(filterType, filterValue) {
    // Special handling for availability filters
    if (filterType === 'availability' || filterType === 'Availability') {
      if (filterValue.toLowerCase().includes('in stock') || filterValue === '1') {
        return 'in-stock';
      } else if (filterValue.toLowerCase().includes('out of stock') || filterValue === '0') {
        return 'out-of-stock';
      }
    }
    
    // Default: convert to URL-friendly format
    return filterValue.toLowerCase().replace(/\s+/g, '-');
  }

  /**
   * Validates and creates collection links with existence checking
   * @param {string} baseCollection - The base collection handle
   * @param {string} filterFragment - The filter fragment to append
   * @returns {Promise<string|null>} The validated URL or null if collection doesn't exist
   */
  async createValidatedCollectionUrl(baseCollection, filterFragment) {
    const testHandle1 = `${baseCollection}-${filterFragment}`;
    const testHandle2 = `${filterFragment}-${baseCollection}`;
    
    // Check both possible collection handle patterns
    const exists1 = await this.checkCollectionExists(testHandle1);
    if (exists1) {
      return `/collections/${testHandle1}`;
    }
    
    const exists2 = await this.checkCollectionExists(testHandle2);
    if (exists2) {
      return `/collections/${testHandle2}`;
    }
    
    return null;
  }

  /**
   * Checks if a collection exists before creating a link
   * @param {string} collectionHandle - The collection handle to check
   * @returns {boolean} True if collection exists, false otherwise
   */
  async checkCollectionExists(collectionHandle) {
    try {
      // Try to fetch the collection page to see if it exists
      const response = await fetch(`/collections/${collectionHandle}`, {
        method: 'HEAD',
        headers: {
          'Accept': 'text/html'
        }
      });
      return response.ok;
    } catch (error) {
      console.warn(`Failed to check if collection ${collectionHandle} exists:`, error);
    return false;
    }
  }

  /**
   * Updates the selected facet summary
   */
  #updateSelectedFacetSummary() {
    if (!this.refs.facetInputs) return;

    const checkedInputElements = this.refs.facetInputs.filter((input) => input.checked);
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.updateListSummary(checkedInputElements);
  }
}

if (!customElements.get('facet-inputs-component')) {
  customElements.define('facet-inputs-component', FacetInputsComponent);
}

/**
 * @typedef {Object} PriceFacetRefs
 * @property {HTMLInputElement} minInput - The minimum price input
 * @property {HTMLInputElement} maxInput - The maximum price input
 */

/**
 * Handles price facet functionality
 * @extends {Component<PriceFacetRefs>}
 */
class PriceFacetComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keydown', this.#onKeyDown);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keydown', this.#onKeyDown);
  }

  /**
   * Handles keydown events to restrict input to valid characters
   * @param {KeyboardEvent} event - The keydown event
   */
  #onKeyDown = (event) => {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  };

  /**
   * Updates price filter and results
   */
  updatePriceFilterAndResults() {
    const { minInput, maxInput } = this.refs;

    this.#adjustToValidValues(minInput);
    this.#adjustToValidValues(maxInput);

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
    this.#setMinAndMaxValues();
    this.#updateSummary();
  }

  /**
   * Adjusts input values to be within valid range
   * @param {HTMLInputElement} input - The input element to adjust
   */
  #adjustToValidValues(input) {
    if (input.value.trim() === '') return;

    const value = Number(input.value);
    const min = Number(formatMoney(input.getAttribute('data-min') ?? ''));
    const max = Number(formatMoney(input.getAttribute('data-max') ?? ''));

    if (value < min) input.value = min.toString();
    if (value > max) input.value = max.toString();
  }

  /**
   * Sets min and max values for the inputs
   */
  #setMinAndMaxValues() {
    const { minInput, maxInput } = this.refs;

    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', '0');
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max') ?? '');
  }

  /**
   * Updates the price summary
   */
  #updateSummary() {
    const { minInput, maxInput } = this.refs;
    const details = this.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent?.updatePriceSummary(minInput, maxInput);
  }
}

if (!customElements.get('price-facet-component')) {
  customElements.define('price-facet-component', PriceFacetComponent);
}

/**
 * Handles clearing of facet filters
 * @extends {Component}
 */
class FacetClearComponent extends Component {
  requiredRefs = ['clearButton'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Clears the filter
   * @param {Event} event - The click event
   */
  clearFilter(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const container = event.target.closest('facet-inputs-component, price-facet-component');
    container?.querySelectorAll('[type="checkbox"]:checked, input').forEach((input) => {
      if (input instanceof HTMLInputElement) {
        input.checked = false;
        input.value = '';
      }
    });

    const details = event.target.closest('details');
    const statusComponent = details?.querySelector('facet-status-component');

    if (!(statusComponent instanceof FacetStatusComponent)) return;

    statusComponent.clearSummary();

    const facetsForm = this.closest('facets-form-component');
    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFilters();
  }

  /**
   * Handles keyup events
   * @param {KeyboardEvent} event - The keyup event
   */
  #handleKeyUp = (event) => {
    if (event.metaKey) return;
    if (event.key === 'Enter') this.clearFilter(event);
  };

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('facets__clear--active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-clear-component')) {
  customElements.define('facet-clear-component', FacetClearComponent);
}

/**
 * @typedef {Object} FacetRemoveComponentRefs
 * @property {HTMLInputElement | undefined} clearButton - The button to clear filters
 */

/**
 * Handles removal of individual facet filters
 * @extends {Component<FacetRemoveComponentRefs>}
 */
class FacetRemoveComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
  }

  /**
   * Removes the filter
   * @param {Object} data - The data object
   * @param {string} data.form - The form to remove the filter from
   * @param {Event} event - The click event
   */
  removeFilter({ form }, event) {
    if (event instanceof KeyboardEvent) {
      if (event.key !== 'Enter' && event.key !== ' ') {
        return;
      }
      event.preventDefault();
    }

    const url = this.dataset.url;
    if (!url) return;

    const facetsForm = form ? document.getElementById(form) : this.closest('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;

    facetsForm.updateFiltersByURL(url);
  }

  /**
   * Toggle clear button visibility when filters are applied. Happens before the
   * Section Rendering Request resolves.
   *
   * @param {FilterUpdateEvent} event
   */
  #handleFilterUpdate = (event) => {
    const { clearButton } = this.refs;
    if (clearButton instanceof Element) {
      clearButton.classList.toggle('active', event.shouldShowClearAll());
    }
  };
}

if (!customElements.get('facet-remove-component')) {
  customElements.define('facet-remove-component', FacetRemoveComponent);
}

/**
 * Handles sorting filter functionality
 *
 * @typedef {Object} SortingFilterRefs
 * @property {HTMLDetailsElement} details - The details element
 * @property {HTMLElement} summary - The summary element
 * @property {HTMLElement} listbox - The listbox element
 *
 * @extends {Component}
 */
class SortingFilterComponent extends Component {
  requiredRefs = ['details', 'summary', 'listbox'];

  /**
   * Handles keyboard navigation in the sorting dropdown
   * @param {KeyboardEvent} event - The keyboard event
   */
  handleKeyDown = (event) => {
    const { listbox } = this.refs;
    if (!(listbox instanceof Element)) return;

    const options = Array.from(listbox.querySelectorAll('[role="option"]'));
    const currentFocused = options.find((option) => option instanceof HTMLElement && option.tabIndex === 0);
    let newFocusIndex = currentFocused ? options.indexOf(currentFocused) : 0;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        newFocusIndex = Math.min(newFocusIndex + 1, options.length - 1);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'ArrowUp':
        event.preventDefault();
        newFocusIndex = Math.max(newFocusIndex - 1, 0);
        this.#moveFocus(options, newFocusIndex);
        break;

      case 'Enter':
      case ' ':
        if (event.target instanceof Element) {
          const targetOption = event.target.closest('[role="option"]');
          if (targetOption) {
            event.preventDefault();
            this.#selectOption(targetOption);
          }
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.#closeDropdown();
        break;
    }
  };

  /**
   * Handles details toggle event
   */
  handleToggle = () => {
    const { details, summary, listbox } = this.refs;
    if (!(details instanceof HTMLDetailsElement) || !(summary instanceof HTMLElement)) return;

    const isOpen = details.open;
    summary.setAttribute('aria-expanded', isOpen.toString());

    if (isOpen && listbox instanceof Element) {
      // Move focus to selected option when dropdown opens
      const selectedOption = listbox.querySelector('[aria-selected="true"]');
      if (selectedOption instanceof HTMLElement) {
        selectedOption.focus();
      }
    }
  };

  /**
   * Moves focus between options
   * @param {Element[]} options - The option elements
   * @param {number} newIndex - The index of the option to focus
   */
  #moveFocus(options, newIndex) {
    // Remove tabindex from all options
    options.forEach((option) => {
      if (option instanceof HTMLElement) {
        option.tabIndex = -1;
      }
    });

    // Set tabindex and focus on new option
    const targetOption = options[newIndex];
    if (targetOption instanceof HTMLElement) {
      targetOption.tabIndex = 0;
      targetOption.focus();
    }
  }

  /**
   * Selects an option and triggers form submission
   * @param {Element} option - The option element to select
   */
  #selectOption(option) {
    const input = option.querySelector('input[type="radio"]');
    if (input instanceof HTMLInputElement && option instanceof HTMLElement) {
      // Update aria-selected states
      this.querySelectorAll('[role="option"]').forEach((opt) => {
        opt.setAttribute('aria-selected', 'false');
      });
      option.setAttribute('aria-selected', 'true');

      // Trigger click on the input to ensure normal form behavior
      input.click();

      // Close dropdown and return focus (handles tabIndex reset)
      this.#closeDropdown();
    }
  }

  /**
   * Closes the dropdown and returns focus to summary
   */
  #closeDropdown() {
    const { details, summary } = this.refs;
    if (details instanceof HTMLDetailsElement) {
      // Reset focus to match the actual selected option
      const options = this.querySelectorAll('[role="option"]');
      const selectedOption = this.querySelector('[aria-selected="true"]');

      options.forEach((opt) => {
        if (opt instanceof HTMLElement) {
          opt.tabIndex = -1;
        }
      });

      if (selectedOption instanceof HTMLElement) {
        selectedOption.tabIndex = 0;
      }

      details.open = false;
      if (summary instanceof HTMLElement) {
        summary.focus();
      }
    }
  }

  /**
   * Updates filter and sorting
   * @param {Event} event - The change event
   */
  updateFilterAndSorting(event) {
    const facetsForm =
      this.closest('facets-form-component') || this.closest('.shopify-section')?.querySelector('facets-form-component');

    if (!(facetsForm instanceof FacetsFormComponent)) return;
    const isMobile = window.innerWidth < 750;

    const shouldDisable = this.dataset.shouldUseSelectOnMobile === 'true';

    // Because we have a select element on mobile and a bunch of radio buttons on desktop,
    // we need to disable the input during "form-submission" to prevent duplicate entries.
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = true;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = true;
      }
    }

    facetsForm.updateFilters();
    this.updateFacetStatus(event);

    // Re-enable the input after the form-submission
    if (shouldDisable) {
      if (isMobile) {
        const inputs = this.querySelectorAll('input[name="sort_by"]');
        inputs.forEach((input) => {
          if (!(input instanceof HTMLInputElement)) return;
          input.disabled = false;
        });
      } else {
        const selectElement = this.querySelector('select[name="sort_by"]');
        if (!(selectElement instanceof HTMLSelectElement)) return;
        selectElement.disabled = false;
      }
    }

    // Close the details element when a value is selected
    const { details } = this.refs;
    if (!(details instanceof HTMLDetailsElement)) return;
    details.open = false;
  }

  /**
   * Updates the facet status
   * @param {Event} event - The change event
   */
  updateFacetStatus(event) {
    if (!(event.target instanceof HTMLSelectElement)) return;

    const details = this.querySelector('details');
    if (!details) return;

    const facetStatus = details.querySelector('facet-status-component');
    if (!(facetStatus instanceof FacetStatusComponent)) return;

    facetStatus.textContent =
      event.target.value !== details.dataset.defaultSortBy ? event.target.dataset.optionName ?? '' : '';
  }
}

if (!customElements.get('sorting-filter-component')) {
  customElements.define('sorting-filter-component', SortingFilterComponent);
}

/**
 * @typedef {Object} FacetStatusRefs
 * @property {HTMLElement} facetStatus - The facet status element
 */

/**
 * Handles facet status display
 * @extends {Component<FacetStatusRefs>}
 */
class FacetStatusComponent extends Component {
  /**
   * Updates the list summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   */
  updateListSummary(checkedInputElements) {
    const checkedInputElementsCount = checkedInputElements.length;

    this.getAttribute('facet-type') === 'swatches'
      ? this.#updateSwatchSummary(checkedInputElements, checkedInputElementsCount)
      : this.#updateBubbleSummary(checkedInputElements, checkedInputElementsCount);
  }

  /**
   * Updates the swatch summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateSwatchSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount > 3) {
      facetStatus.innerHTML = checkedInputElementsCount.toString();
      facetStatus.classList.add('bubble', 'facets__bubble');
      return;
    }

    facetStatus.innerHTML = Array.from(checkedInputElements)
      .map((inputElement) => {
        const swatch = inputElement.parentElement?.querySelector('span.swatch');
        return swatch?.outerHTML ?? '';
      })
      .join('');
  }

  /**
   * Updates the bubble summary
   * @param {HTMLInputElement[]} checkedInputElements - The checked input elements
   * @param {number} checkedInputElementsCount - The number of checked inputs
   */
  #updateBubbleSummary(checkedInputElements, checkedInputElementsCount) {
    const { facetStatus } = this.refs;
    facetStatus.classList.remove('bubble', 'facets__bubble');

    if (checkedInputElementsCount === 0) {
      facetStatus.innerHTML = '';
      return;
    }

    if (checkedInputElementsCount === 1) {
      facetStatus.innerHTML = checkedInputElements[0]?.dataset.label ?? '';
      return;
    }

    facetStatus.innerHTML = checkedInputElementsCount.toString();
    facetStatus.classList.add('bubble', 'facets__bubble');
  }

  /**
   * Updates the price summary
   * @param {HTMLInputElement} minInput - The minimum price input
   * @param {HTMLInputElement} maxInput - The maximum price input
   */
  updatePriceSummary(minInput, maxInput) {
    const minInputValue = minInput.value;
    const maxInputValue = maxInput.value;
    const { facetStatus } = this.refs;

    if (!minInputValue && !maxInputValue) {
      facetStatus.innerHTML = '';
      return;
    }

    const minInputNum = this.#parseCents(minInputValue, '0');
    const maxInputNum = this.#parseCents(maxInputValue, facetStatus.dataset.rangeMax);
    facetStatus.innerHTML = `${this.#formatMoney(minInputNum)}–${this.#formatMoney(maxInputNum)}`;
  }

  /**
   * Parses a decimal number as cents
   * @param {string} value - The stringified decimal number to parse
   * @param {string} fallback - The fallback value in case `value` is invalid
   * @returns {number} The money value in cents
   */
  #parseCents(value, fallback = '0') {
    const parts = value ? value.trim().split(/[^0-9]/) : (parseInt(fallback, 10) / 100).toString();
    const [wholeStr, fractionStr, ...rest] = parts;
    if (typeof wholeStr !== 'string' || rest.length > 0) return parseInt(fallback, 10);

    const whole = parseInt(wholeStr, 10);
    let fraction = parseInt(fractionStr || '0', 10);

    // Use two most-significant digits, e.g. 1 -> 10, 12 -> 12, 123 -> 12.3, 1234 -> 12.34, etc
    fraction = fraction * Math.pow(10, 2 - fraction.toString().length);

    return whole * 100 + fraction;
  }

  /**
   * Formats money, replicated the implementation of the `money` liquid filters
   * @param {number} moneyValue - The money value
   * @returns {string} The formatted money value
   */
  #formatMoney(moneyValue) {
    if (!(this.refs.moneyFormat instanceof HTMLTemplateElement)) return '';

    const template = this.refs.moneyFormat.content.textContent || '{{amount}}';
    const currency = this.refs.facetStatus.dataset.currency || '';

    return template.replace(/{{\s*(\w+)\s*}}/g, (_, placeholder) => {
      if (typeof placeholder !== 'string') return '';
      if (placeholder === 'currency') return currency;

      let thousandsSeparator = ',';
      let decimalSeparator = '.';
      let precision = CURRENCY_DECIMALS[currency.toUpperCase()] ?? DEFAULT_CURRENCY_DECIMALS;

      if (placeholder === 'amount') {
        // Check first since it's the most common, use defaults.
      } else if (placeholder === 'amount_no_decimals') {
        precision = 0;
      } else if (placeholder === 'amount_with_comma_separator') {
        thousandsSeparator = '.';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_no_decimals_with_comma_separator') {
        // Weirdly, this is correct. It uses amount_with_comma_separator's
        // behaviour but removes decimals, resulting in an unintuitive
        // output that can't possibly include commas, despite the name.
        thousandsSeparator = '.';
        precision = 0;
      } else if (placeholder === 'amount_no_decimals_with_space_separator') {
        thousandsSeparator = ' ';
        precision = 0;
      } else if (placeholder === 'amount_with_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = ',';
      } else if (placeholder === 'amount_with_period_and_space_separator') {
        thousandsSeparator = ' ';
        decimalSeparator = '.';
      } else if (placeholder === 'amount_with_apostrophe_separator') {
        thousandsSeparator = "'";
        decimalSeparator = '.';
      }

      return this.#formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision);
    });
  }

  /**
   * Formats money in cents
   * @param {number} moneyValue - The money value in cents (hundredths of one major currency unit)
   * @param {string} thousandsSeparator - The thousands separator
   * @param {string} decimalSeparator - The decimal separator
   * @param {number} precision - The precision
   * @returns {string} The formatted money value
   */
  #formatCents(moneyValue, thousandsSeparator, decimalSeparator, precision) {
    const roundedNumber = (moneyValue / 100).toFixed(precision);

    let [a, b] = roundedNumber.split('.');
    if (!a) a = '0';
    if (!b) b = '';

    // Split by groups of 3 digits
    a = a.replace(/\d(?=(\d\d\d)+(?!\d))/g, (digit) => digit + thousandsSeparator);

    return precision <= 0 ? a : a + decimalSeparator + b.padEnd(precision, '0');
  }

  /**
   * Clears the summary
   */
  clearSummary() {
    this.refs.facetStatus.innerHTML = '';
  }
}

if (!customElements.get('facet-status-component')) {
  customElements.define('facet-status-component', FacetStatusComponent);
}

/**
 * Default currency decimals used in most currenies
 * @constant {number}
 */
const DEFAULT_CURRENCY_DECIMALS = 2;

/**
 * Decimal precision for currencies that have a non-default precision
 * @type {Record<string, number>}
 */
const CURRENCY_DECIMALS = {
  BHD: 3,
  BIF: 0,
  BYR: 0,
  CLF: 4,
  CLP: 0,
  DJF: 0,
  GNF: 0,
  IQD: 3,
  ISK: 0,
  JOD: 3,
  JPY: 0,
  KMF: 0,
  KRW: 0,
  KWD: 3,
  LYD: 3,
  MRO: 5,
  OMR: 3,
  PYG: 0,
  RWF: 0,
  TND: 3,
  UGX: 0,
  UYI: 0,
  UYW: 4,
  VND: 0,
  VUV: 0,
  XAF: 0,
  XAG: 0,
  XAU: 0,
  XBA: 0,
  XBB: 0,
  XBC: 0,
  XBD: 0,
  XDR: 0,
  XOF: 0,
  XPD: 0,
  XPF: 0,
  XPT: 0,
  XSU: 0,
  XTS: 0,
  XUA: 0,
};